import { useMemo } from 'react'
import { usePartnerAuth } from '../context/PartnerAuthContext'

/**
 * usePartnerTheme — Fournit les variables de thème dérivées du color_code du partenaire.
 *
 * Utilisé dans le layout partenaire pour appliquer dynamiquement:
 * - header gradient
 * - footer background
 * - accents (boutons, badges)
 */

export type PartnerTheme = {
  /** Couleur principale du partenaire (hex) */
  primary: string
  /** Version assombrie pour les gradients */
  primaryDark: string
  /** Version très claire pour les fonds */
  primaryLight: string
  /** Gradient CSS pour le header */
  headerGradient: string
  /** Background du footer */
  footerBackground: string
  /** Couleur CSS pour les accents (liens, boutons) */
  accent: string
}

/** Assombrit une couleur hex d'un facteur (0-1) */
function darken(hex: string, factor: number): string {
  const h = hex.replace('#', '')
  const r = Math.max(0, Math.round(parseInt(h.substring(0, 2), 16) * (1 - factor)))
  const g = Math.max(0, Math.round(parseInt(h.substring(2, 4), 16) * (1 - factor)))
  const b = Math.max(0, Math.round(parseInt(h.substring(4, 6), 16) * (1 - factor)))
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

/** Éclaircit une couleur hex d'un facteur (0-1) */
function lighten(hex: string, factor: number): string {
  const h = hex.replace('#', '')
  const r = Math.min(255, Math.round(parseInt(h.substring(0, 2), 16) + (255 - parseInt(h.substring(0, 2), 16)) * factor))
  const g = Math.min(255, Math.round(parseInt(h.substring(2, 4), 16) + (255 - parseInt(h.substring(2, 4), 16)) * factor))
  const b = Math.min(255, Math.round(parseInt(h.substring(4, 6), 16) + (255 - parseInt(h.substring(4, 6), 16)) * factor))
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

const DEFAULT_COLOR = '#1976d2'

export function usePartnerTheme(): PartnerTheme {
  const { partner } = usePartnerAuth()

  return useMemo(() => {
    const color = partner?.color_code || DEFAULT_COLOR
    const dark = darken(color, 0.35)
    const light = lighten(color, 0.92)

    return {
      primary: color,
      primaryDark: dark,
      primaryLight: light,
      headerGradient: `linear-gradient(135deg, ${color} 0%, ${dark} 100%)`,
      footerBackground: dark,
      accent: color,
    }
  }, [partner?.color_code])
}
